import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Typography,
} from "@material-ui/core"
import CancelIcon from "@material-ui/icons/Cancel"
import CloseIcon from "@material-ui/icons/Close"
import LocalShippingIcon from "@material-ui/icons/LocalShipping"
import OpenInNewIcon from "@material-ui/icons/OpenInNew"
import RefreshIcon from "@material-ui/icons/Refresh"
import ReplayIcon from "@material-ui/icons/Replay"
import { useCallback, useEffect, useState } from "react"
import { FLUX_LOGO_URL } from "../../../IntegracaoFlux/assets"
import {
    acionarNovoEntregadorFlux,
    buscarPainelEntregaFlux,
    cancelarEntregaFlux,
    reenviarEntregaFlux,
} from "../services"

// statusEntrega cancelado (cancelado pela loja "CANCELADA" ou status de cancelamento/devolução da
// Flux) — cabe alocar um novo entregador. "CANCELADA" também casa "ENTREGA_CANCELADA(_LOJA)".
const STATUS_CANCELADOS = ["CANCELADA", "NAO_COLETADO", "DEVOLVIDO"]
// Status terminais de sucesso (entrega concluída / retorno à loja) — não cabe acionar/cancelar.
const STATUS_CONCLUIDOS = ["ENTREGA_REALIZADA", "RETORNO_LOJA"]
// statusEntrega logo após criar a entrega na Flux (aguardando alocação do entregador).
const STATUS_CRIADA = "CRIADA"

const statusInclui = (status, tokens) => {
    if (!status) return false
    const s = String(status).toUpperCase()
    return tokens.some(t => s.includes(t))
}

// Rótulos amigáveis para o statusEntrega (interno + status crus da Flux).
const STATUS_LABELS = {
    CRIADA: "Aguardando alocação",
    EM_ROTA_DE_COLETA: "A caminho da coleta",
    CHEGADA_NA_COLETA: "No local de coleta",
    DESPACHADO: "A caminho da entrega",
    CHEGADA_NA_ENTREGA: "No local de entrega",
    ENTREGA_REALIZADA_COM_SUCESSO: "Entregue",
    ENTREGA_REALIZADA: "Entregue",
    RETORNO_LOJA: "Retornado à loja",
    NAO_COLETADO: "Não coletado",
    DEVOLVIDO: "Devolvido",
    CANCELADA: "Cancelada",
    ENTREGA_CANCELADA: "Cancelada",
    ENTREGA_CANCELADA_LOJA: "Cancelada pela loja",
}

// Converte o status cru num rótulo legível; se desconhecido, formata o enum (CAIXA_ALTA -> Caixa Alta).
const formatarStatusEntrega = status => {
    if (!status) return null
    const chave = String(status).toUpperCase().trim()
    if (STATUS_LABELS[chave]) return STATUS_LABELS[chave]
    return chave
        .toLowerCase()
        .replace(/_/g, " ")
        .replace(/\b\w/g, c => c.toUpperCase())
}

// Deixa a mensagem de erro da Flux mais legível: tira o prefixo "ERRO" e, quando o motivo é um
// JSON de erro HTTP, destaca o campo "error"/"message" — mantendo abaixo a resposta COMPLETA da Flux
// (para diagnóstico/log, nunca só o resumo).
function formatarErro(mensagem) {
    if (!mensagem) return null
    const texto = String(mensagem)
        .replace(/^ERRO\s+INESPERADO\s+/i, "Erro inesperado: ")
        .replace(/^ERRO:?\s+/i, "")
        .trim()
    const ini = texto.indexOf("{")
    if (ini >= 0) {
        try {
            const obj = JSON.parse(texto.slice(ini))
            const partes = []
            if (obj.error) partes.push(obj.error)
            if (obj.message && obj.message !== obj.error) partes.push(obj.message)
            if (partes.length) return `${partes.join(" — ")}\n\nResposta completa: ${texto}`
        } catch {
            /* mantém o texto cru */
        }
    }
    return texto
}

// Resposta de erro genérica do servidor ({mensagem, codErro, token}): exceção não tratada no backend.
// Mostra a mensagem com o id do log, para achar a pilha no servidor.
function erroDoServidor(data) {
    if (!data || !(data.codErro > 0)) return null
    return `${data.mensagem || "Erro no servidor."}${
        data.token ? ` (id do log: ${data.token})` : ""
    }`
}

// Normaliza a resposta de /obter-codigos-verificacao. Formato da Flux:
// { codigoColeta, codigoRetorno, codigoConfirmacaoCliente, nomeEntregador, whatsappEntregador }.
// Tolera aninhamento em codigosEntrega/codigosVerificacao/data e alguns aliases.
function extrairFonte(payload) {
    if (!payload || typeof payload !== "object") return null
    return payload.codigosEntrega || payload.codigosVerificacao || payload.data || payload
}

function extrairCodigos(payload) {
    const fonte = extrairFonte(payload)
    if (!fonte) return null
    const codigos = {
        cliente: fonte.codigoConfirmacaoCliente ?? fonte.cliente ?? fonte.codigoCliente ?? null,
        coleta: fonte.codigoColeta ?? fonte.coleta ?? null,
        retorno: fonte.codigoRetorno ?? fonte.retorno ?? null,
    }
    if (codigos.cliente == null && codigos.coleta == null && codigos.retorno == null) return null
    return codigos
}

function extrairEntregador(payload) {
    const fonte = extrairFonte(payload)
    if (!fonte) return null
    const nome = fonte.nomeEntregador ?? null
    const telefone = fonte.whatsappEntregador ?? fonte.telefoneEntregador ?? null
    if (nome == null && telefone == null) return null
    return { nome, telefone }
}

// Fallback: extrai os códigos do texto salvo em observacao
// ("Códigos de entrega — cliente: 7488 | coleta: 7740 | retorno: 3449").
function extrairCodigosDaObservacao(obs) {
    if (!obs || !/c[óo]digos/i.test(obs)) return null
    const pega = re => {
        const m = obs.match(re)
        return m ? m[1].trim() : null
    }
    const codigos = {
        cliente: pega(/cliente:\s*([^\s|]+)/i),
        coleta: pega(/coleta:\s*([^\s|]+)/i),
        retorno: pega(/retorno:\s*([^\s|]+)/i),
    }
    if (codigos.cliente == null && codigos.coleta == null && codigos.retorno == null) return null
    return codigos
}

// Linha compacta "Rótulo: valor" (uma por linha). Mostra "—" quando o valor está ausente.
function LinhaInfo({ rotulo, valor }) {
    // Trata null/vazio e também a string literal "null"/"undefined" (dados legados) como sem valor.
    const vazio =
        valor == null ||
        valor === "" ||
        (typeof valor === "string" && ["null", "undefined"].includes(valor.trim().toLowerCase()))
    return (
        <Typography variant="body2" style={{ margin: "3px 0", color: "#2c3e50" }}>
            <span style={{ color: "#7f8c8d" }}>{rotulo}: </span>
            <span style={{ fontWeight: 600 }}>{vazio ? "—" : valor}</span>
        </Typography>
    )
}

export default function DetalhesEntregaFlux({ aberto, onClose, pedido, autorizacao }) {
    const [carregando, setCarregando] = useState(false)
    const [atualizando, setAtualizando] = useState(false)
    const [detalhes, setDetalhes] = useState(null)
    const [statusFlux, setStatusFlux] = useState(null)
    const [codigos, setCodigos] = useState(null)
    const [entregadorFlux, setEntregadorFlux] = useState(null)
    const [erroEnvio, setErroEnvio] = useState(null)
    const [reenviando, setReenviando] = useState(false)
    const [sucessoReenvio, setSucessoReenvio] = useState(null)
    const [acionamentoAutomatico, setAcionamentoAutomatico] = useState(true)
    const [acionando, setAcionando] = useState(false)
    const [cancelando, setCancelando] = useState(false)
    const [erroAcao, setErroAcao] = useState(null)

    // Uma chamada só ao backend. Ao abrir usa o que o webhook já gravou; atualizar=true (botão
    // "Atualizar") consulta o último status e os códigos direto na Flux.
    const carregar = useCallback(
        async (atualizar = false) => {
            if (!pedido?.id) return
            const res = await buscarPainelEntregaFlux(pedido.id, autorizacao, atualizar)
            const dados = res?.data || {}
            if (dados.erro) {
                setErroAcao(dados.erro)
                return
            }
            setDetalhes(dados.detalhes || null)
            const statusPayload = dados.status
            const statusTexto =
                statusPayload?.status ||
                statusPayload?.rastreio ||
                statusPayload?.ultimoStatus ||
                null
            setStatusFlux(statusTexto)
            setCodigos(extrairCodigos(dados.codigos))
            setEntregadorFlux(extrairEntregador(dados.codigos))
            const situacao = dados.situacao
            setErroEnvio(
                situacao && situacao.existe && situacao.sucesso === false && situacao.mensagem
                    ? formatarErro(situacao.mensagem)
                    : null
            )
            // Padrão automático quando o backend não informar (ex.: versão antiga).
            setAcionamentoAutomatico(situacao?.acionamentoAutomatico !== false)
        },
        [pedido?.id, autorizacao]
    )

    useEffect(() => {
        if (!aberto) return
        setSucessoReenvio(null)
        setErroAcao(null)
        setCarregando(true)
        carregar().finally(() => setCarregando(false))
    }, [aberto, carregar])

    const handleAtualizar = async () => {
        setAtualizando(true)
        setErroAcao(null)
        setSucessoReenvio(null)
        try {
            await carregar(true)
        } finally {
            setAtualizando(false)
        }
    }

    const handleReenviar = async () => {
        if (!pedido?.id) return
        setReenviando(true)
        setSucessoReenvio(null)
        try {
            const res = await reenviarEntregaFlux(pedido.id, autorizacao)
            const situacao = res?.data || {}
            if (situacao.sucesso) {
                setErroEnvio(null)
                setSucessoReenvio("Pedido reenviado com sucesso para a Flux.")
                await carregar()
            } else {
                setErroEnvio(
                    erroDoServidor(situacao) ||
                        formatarErro(situacao.mensagem) ||
                        "Falha ao reenviar o pedido."
                )
            }
        } finally {
            setReenviando(false)
        }
    }

    const handleAcionarEntregador = async () => {
        if (!pedido?.id) return
        setAcionando(true)
        setErroAcao(null)
        setSucessoReenvio(null)
        try {
            const res = await acionarNovoEntregadorFlux(pedido.id, autorizacao)
            const situacao = res?.data || {}
            if (situacao.sucesso) {
                setErroEnvio(null)
                setSucessoReenvio("Entregador acionado na Flux com sucesso.")
                await carregar()
            } else {
                setErroAcao(
                    erroDoServidor(situacao) ||
                        formatarErro(situacao.mensagem) ||
                        "Falha ao acionar o entregador."
                )
            }
        } finally {
            setAcionando(false)
        }
    }

    const handleCancelarEntrega = async () => {
        if (!pedido?.id) return
        if (
            !window.confirm(
                "Cancelar apenas a solicitação de entrega na Flux? O pedido permanece na situação atual."
            )
        )
            return
        setCancelando(true)
        setErroAcao(null)
        setSucessoReenvio(null)
        try {
            const res = await cancelarEntregaFlux(pedido.id, autorizacao)
            const situacao = res?.data || {}
            if (situacao.sucesso) {
                setSucessoReenvio("Entrega cancelada na Flux.")
                await carregar()
            } else {
                setErroAcao(
                    erroDoServidor(situacao) ||
                        formatarErro(situacao.mensagem) ||
                        "Falha ao cancelar a entrega."
                )
            }
        } finally {
            setCancelando(false)
        }
    }

    // Dados do próprio pedido (topo do diálogo).
    const nomeClientePedido = pedido?.customer?.name || pedido?.nomeCliente || pedido?.nome || null
    const sequencial =
        pedido?.shortReference != null && pedido?.shortReference !== ""
            ? `#${pedido.shortReference}`
            : null

    const link = detalhes?.urlAcompanhamento
    // Só há "retorno" da Flux quando existe um link de rastreio válido (http/https).
    const temLinkRastreio = typeof link === "string" && /^https?:\/\//i.test(link.trim())
    const status = detalhes?.status || statusFlux
    const nomeEntregador = detalhes?.nomeEntregador || entregadorFlux?.nome
    const telefoneEntregador = detalhes?.telefoneEntregador || entregadorFlux?.telefone
    // Preferimos os códigos do endpoint ao vivo; se não vierem, extraímos da observação salva.
    const codigosFinais = codigos || extrairCodigosDaObservacao(detalhes?.observacao)

    // statusEntrega (DetalhesEntrega.status) guia a UI: vazio, CRIADA, cancelado, concluído ou andamento.
    const statusVazio = !status
    const entregaCriada = statusInclui(status, [STATUS_CRIADA])
    const entregaCancelada = statusInclui(status, STATUS_CANCELADOS)
    const entregaConcluida = statusInclui(status, STATUS_CONCLUIDOS)
    // Entregador alocado: já passou do CRIADA para um status de andamento (ou a Flux já mandou o nome).
    const entregadorAlocado =
        !!nomeEntregador ||
        (!statusVazio && !entregaCriada && !entregaCancelada && !entregaConcluida)
    // Permite cancelar enquanto ainda aloca (CRIADA) ou com entregador já alocado — nunca em estado
    // cancelado/concluído. Se a Flux recusar o cancelamento, o erro é exibido na própria tela.
    const podeCancelar = (entregaCriada || entregadorAlocado) && !entregaCancelada && !entregaConcluida
    // Status do pedido na Cliente Fiel: ENTREGUE (4) ou cancelado (< 0) => pedido finalizado. Nesse
    // caso não se aciona/cancela entregador; só mostramos o link de rastreio se existir.
    const pedidoFinalizado = pedido?.status === 4 || pedido?.status < 0

    // Botão de cancelar a entrega (contorno vermelho), reutilizado nos estados que permitem cancelar.
    const botaoCancelar = () => (
        <Button
            size="small"
            variant="outlined"
            startIcon={
                cancelando ? (
                    <CircularProgress size={14} color="inherit" />
                ) : (
                    <CancelIcon fontSize="small" />
                )
            }
            disabled={cancelando}
            onClick={handleCancelarEntrega}
            style={{
                borderColor: "var(--cor-erro)",
                color: "var(--cor-erro)",
                whiteSpace: "nowrap",
                textTransform: "none",
            }}
        >
            {cancelando ? "Cancelando..." : "Cancelar"}
        </Button>
    )

    // Botão de alocar/realocar entregador (envio manual/síncrono, nunca via job).
    const botaoAlocar = rotulo => (
        <Button
            fullWidth
            variant="contained"
            disableElevation
            startIcon={
                acionando ? <CircularProgress size={16} color="inherit" /> : <LocalShippingIcon />
            }
            disabled={acionando}
            onClick={handleAcionarEntregador}
            style={{ backgroundColor: "#1565c0", color: "white" }}
        >
            {acionando ? "Acionando..." : rotulo}
        </Button>
    )

    // Botão de acompanhar a entrega (link de rastreio) — mostrado sempre que houver link.
    const botaoAcompanhar = () => (
        <Button
            style={{ flexGrow: 1, textTransform: "none" }}
            size="small"
            color="primary"
            variant="contained"
            disableElevation
            startIcon={<OpenInNewIcon fontSize="small" />}
            href={link}
            target="_blank"
            rel="noopener noreferrer"
        >
            Acompanhar
        </Button>
    )

    // Ação/estado da entrega. O botão "Acompanhar" aparece sempre que houver link, inclusive quando o
    // pedido já foi finalizado; o "Alocar novo entregador" NÃO aparece se o pedido está entregue/cancelado.
    let acaoEntrega
    if (pedidoFinalizado || entregaConcluida) {
        // Pedido entregue/cancelado na Cliente Fiel (ou entrega já concluída): sem acionar/cancelar;
        // só o link de rastreio, se existir.
        acaoEntrega = temLinkRastreio ? botaoAcompanhar() : null
    } else if (entregaCancelada) {
        acaoEntrega = (
            <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
                {botaoAlocar("Alocar novo entregador")}
                {temLinkRastreio && botaoAcompanhar()}
            </Box>
        )
    } else if (entregaCriada) {
        acaoEntrega = (
            <Box display="flex" flexDirection="column" style={{ gap: 10 }}>
                <Box display="flex" alignItems="center" style={{ gap: 10 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" style={{ color: "#7f8c8d" }}>
                        Aguardando alocação do entregador
                    </Typography>
                </Box>
                <Box display="flex" alignItems="stretch" style={{ gap: 8 }}>
                    {podeCancelar && botaoCancelar()}
                    {temLinkRastreio && botaoAcompanhar()}
                </Box>
            </Box>
        )
    } else if (statusVazio) {
        acaoEntrega = acionamentoAutomatico ? (
            <Typography variant="body2" style={{ color: "#7f8c8d" }}>
                Entregador ainda não alocado para esse pedido
            </Typography>
        ) : (
            botaoAlocar("Alocar entregador")
        )
    } else if (temLinkRastreio || podeCancelar) {
        acaoEntrega = (
            <Box display="flex" alignItems="stretch" style={{ gap: 8 }}>
                {podeCancelar && (
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={
                            cancelando ? (
                                <CircularProgress size={14} color="inherit" />
                            ) : (
                                <CancelIcon fontSize="small" />
                            )
                        }
                        disabled={cancelando}
                        onClick={handleCancelarEntrega}
                        style={{ borderColor: "var(--cor-erro)", color: "var(--cor-erro)", whiteSpace: "nowrap" }}
                    >
                        {cancelando ? "..." : "Cancelar"}
                    </Button>
                )}
                {temLinkRastreio && botaoAcompanhar()}
            </Box>
        )
    } else {
        acaoEntrega = null
    }

    return (
        <Dialog open={aberto} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle disableTypography style={{ paddingBottom: 8 }}>
                <Box display="flex" alignItems="center" style={{ gap: 10 }}>
                    <img src={FLUX_LOGO_URL} alt="Flux" style={{ height: 24, borderRadius: 4 }} />
                    <Typography variant="h6" style={{ flexGrow: 1, fontSize: 18 }}>
                        Detalhes da entrega — Flux
                    </Typography>
                    <IconButton size="small" aria-label="Fechar" onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                {carregando ? (
                    <Grid container justifyContent="center" style={{ padding: 24 }}>
                        <CircularProgress size={28} />
                    </Grid>
                ) : (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <LinhaInfo rotulo="Cliente" valor={nomeClientePedido} />
                            <LinhaInfo rotulo="Sequencial" valor={sequencial} />
                            <LinhaInfo rotulo="Identificador" valor={pedido?.id} />
                            <LinhaInfo rotulo="Status da entrega" valor={formatarStatusEntrega(status)} />
                            {nomeEntregador && (
                                <LinhaInfo rotulo="Entregador" valor={nomeEntregador} />
                            )}
                            {telefoneEntregador && (
                                <LinhaInfo rotulo="WhatsApp" valor={telefoneEntregador} />
                            )}
                            <LinhaInfo rotulo="Código de coleta" valor={codigosFinais?.coleta} />
                            <LinhaInfo rotulo="Código de Retorno" valor={codigosFinais?.retorno} />
                        </Grid>

                        {erroEnvio && (
                            <Grid item xs={12}>
                                <div
                                    style={{
                                        backgroundColor: "#fdecea",
                                        border: "1px solid #f5c6cb",
                                        borderRadius: 6,
                                        padding: "10px 12px",
                                    }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        style={{ color: "var(--cor-erro)", fontWeight: 700 }}
                                    >
                                        Falha ao enviar o pedido para a Flux
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        style={{
                                            color: "#8a1c1c",
                                            wordBreak: "break-word",
                                            whiteSpace: "pre-wrap",
                                        }}
                                    >
                                        {erroEnvio}
                                    </Typography>
                                    <Box mt={1.5}>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            disableElevation
                                            startIcon={
                                                reenviando ? (
                                                    <CircularProgress size={16} color="inherit" />
                                                ) : (
                                                    <ReplayIcon />
                                                )
                                            }
                                            disabled={reenviando}
                                            onClick={handleReenviar}
                                            style={{
                                                backgroundColor: reenviando ? "var(--cor-erro)" : "var(--cor-erro)",
                                                color: "white",
                                            }}
                                        >
                                            {reenviando ? "Reenviando..." : "Reenviar para a Flux"}
                                        </Button>
                                    </Box>
                                </div>
                            </Grid>
                        )}

                        {sucessoReenvio && (
                            <Grid item xs={12}>
                                <div
                                    style={{
                                        backgroundColor: "#e8f5e9",
                                        border: "1px solid #a5d6a7",
                                        borderRadius: 6,
                                        padding: "10px 12px",
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        style={{ color: "#2e7d32", fontWeight: 600 }}
                                    >
                                        {sucessoReenvio}
                                    </Typography>
                                </div>
                            </Grid>
                        )}

                        {erroAcao && (
                            <Grid item xs={12}>
                                <div
                                    style={{
                                        backgroundColor: "#fdecea",
                                        border: "1px solid #f5c6cb",
                                        borderRadius: 6,
                                        padding: "10px 12px",
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        style={{
                                            color: "#8a1c1c",
                                            wordBreak: "break-word",
                                            whiteSpace: "pre-wrap",
                                        }}
                                    >
                                        {erroAcao}
                                    </Typography>
                                </div>
                            </Grid>
                        )}

                        {acaoEntrega && <Grid item xs={12}>{acaoEntrega}</Grid>}

                        {(detalhes || statusFlux) && (
                            <Grid item xs={12}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={
                                        atualizando ? (
                                            <CircularProgress size={14} color="inherit" />
                                        ) : (
                                            <RefreshIcon fontSize="small" />
                                        )
                                    }
                                    disabled={atualizando}
                                    onClick={handleAtualizar}
                                    style={{ textTransform: "none" }}
                                >
                                    {atualizando ? "Atualizando..." : "Atualizar na Flux"}
                                </Button>
                            </Grid>
                        )}
                    </Grid>
                )}
            </DialogContent>
        </Dialog>
    )
}
